import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { initCsrf } from '@/utils/csrf';
import Home from '@/pages/Home';
import LoginPage from '@pages/Login';
import SignupPage from '@pages/Signup';
import SetPassword from '@pages/SetPassword';
import ResendVerification from '@pages/ResendVerification';
import EmailConfirmation from '@pages/ConfirmEmail';
// import PropertyManagerDashboard from '@components/property_managers/PropertyManagerDashboard';
import { InteractiveViewerRefactored } from '@/features/interactive-viewer-v2';
import SharedHotspotViewer from '@/features/interactive-viewer-v2/SharedHotspotViewer';
import { DroneViewer } from '@/features/drones';
import { CreateSurveillanceReport, SurveillanceDashboard } from '@/features/surveillance-reports';
import { ProjectTeam, AcceptInvitation } from '@/features/project-team';
import PrivateRoute from '@components/auth/PrivateRoute';
import { Toaster } from '@components/ui/toaster';
import CookieConsent from '@components/shared/CookieConsent';
import ResetPassword from '@pages/ResetPassword';
import ResetPasswordConfirm from '@pages/ResetPasswordConfirm';
import '@styles/theme-variables.css';
import { PropertyProvider } from './context/PropertyContext';
import Dashboard from '@/pages/Dashboard';
import AllProjects from '@/pages/AllProjects';
import AllClients from '@/pages/AllClients';
import ScheduleVisit from '@/pages/ScheduleVisit';
import MyAccount from '@/pages/MyAccount';
import { AddScanPage } from '@/features/add-scan';
import { AddLayoutPage } from '@/features/add-layout';
import { PDFEditor } from '@/features/pdf-editor';
import UploadDronePage from '@/pages/UploadDronePage';
import { useUser } from '@/shared/hooks/useUser';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';
import {
  PunchListsPage,
  PunchListDetail,
  TemplateBuilder,
  PunchListsDashboard,
} from '@/features/punch-lists/components';
import { LeadsCapturePage } from '@/features/leads-capture';

// TODO: Add a route for viewing an individual team member - JC

// Component to handle dashboard redirect
const DashboardRedirect: React.FC = () => {
  const { user, loading } = useUser();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <LogoLoading 
          variant="breathing" 
          size="xl" 
          message="Loading your account..." 
        />
      </div>
    );
  }
  
  if (user?.client_info?.name) {
    return <Navigate to={`/app/dashboard/${user.client_info.name}`} replace />;
  }
  
  // Fallback if no client is found
  return <Navigate to="/" replace />;
};

function App() {
  // Fetch and store the CSRF token on app mount, exactly as the article prescribes.
  // This runs once regardless of auth state so every subsequent POST/PUT/PATCH/DELETE
  // has the token ready in memory before the user interacts with anything.
  useEffect(() => {
    initCsrf().catch((err) => console.error('Failed to initialise CSRF token:', err));
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/accounts/login" element={<Navigate to="/login" replace />} />
        <Route path="/accounts/signup" element={<Navigate to="/signup" replace />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/shared-hotspot/:accessToken" element={<SharedHotspotViewer />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password/:uidb64/:token" element={<ResetPasswordConfirm />} />
        <Route path="/set-password/:token" element={<SetPassword />} />
        <Route path="/verification-sent" element={<ResendVerification />} />
        <Route path="/confirm-email/:key" element={<EmailConfirmation />} />
        {/* Protected Routes */}
        <Route path="/app" element={<PrivateRoute />}>
          {/* Default dashboard redirect */}
          <Route index element={<DashboardRedirect />} />
          <Route path="dashboard" element={<DashboardRedirect />} />
          
          {/* Dashboard with client name */}
          <Route path="dashboard/:clientName" element={<Dashboard />} />
          
          {/* My Account / Settings */}
          <Route path="account" element={<MyAccount />} />
          
          {/* General protected routes */}
          <Route path="vendors" element={<div>Vendors</div>} />
          
          {/* All Projects page (staff only) */}
          <Route path="projects/all" element={<AllProjects />} />
          
          {/* All Clients page (staff only) */}
          <Route path="clients/all" element={<AllClients />} />
          
          {/* Schedule Visit page */}
          <Route path="schedule-visit" element={<ScheduleVisit />} />
          
          {/* Scan routes */}
          <Route path="scan/add" element={<AddScanPage />} />
          
          {/* Layout routes */}
          <Route path="layout/add" element={<AddLayoutPage />} />
          
          {/* Drone routes */}
          <Route path="drone/upload" element={<UploadDronePage />} />
          
          {/* Surveillance routes */}
          <Route path="surveillance/create" element={<CreateSurveillanceReport />} />
          <Route path="surveillance/:clientName/:projectName" element={<SurveillanceDashboard />} />

          {/* Leads capture (staff only) */}
          <Route path="leads/capture" element={<LeadsCapturePage />} />
          
          {/* Team routes */}
          <Route path="team/:clientName/:projectName" element={<ProjectTeam />} />
          
          {/* Property-related routes (wrapped with PropertyProvider) */}
          <Route
            path="property/*"
            element={
              <PropertyProvider>
                <Routes>
                  <Route path="documentation" element={<div>Property Documentation</div>} />
                  {/* Legacy routes (no clientName) - kept for bookmark support */}
                  <Route path=":projectId/asbuilds" element={<InteractiveViewerRefactored />} />
                  <Route path=":projectId/progress" element={<InteractiveViewerRefactored />} />
                  <Route path=":projectId/drones" element={<DroneViewer />} />
                  <Route path=":projectId/drawings" element={<PDFEditor />} />
                  <Route path=":projectId/punch-lists" element={<PunchListsPage />} />
                  <Route path=":projectId/punch-lists/templates" element={<PunchListsDashboard />} />
                  <Route path=":projectId/punch-lists/:punchlistId" element={<PunchListDetail />} />
                  <Route path=":projectId/punch-lists/templates/new" element={<TemplateBuilder />} />
                  <Route path=":projectId/punch-lists/templates/:templateId" element={<TemplateBuilder />} />
                  {/* New routes with clientName for sidebar context */}
                  <Route path=":clientName/:projectId/asbuilds" element={<InteractiveViewerRefactored />} />
                  <Route path=":clientName/:projectId/progress" element={<InteractiveViewerRefactored />} />
                  <Route path=":clientName/:projectId/drones" element={<DroneViewer />} />
                  <Route path=":clientName/:projectId/drawings" element={<PDFEditor />} />
                  <Route path=":clientName/:projectId/punch-lists" element={<PunchListsPage />} />
                  <Route path=":clientName/:projectId/punch-lists/templates" element={<PunchListsDashboard />} />
                  <Route path=":clientName/:projectId/punch-lists/:punchlistId" element={<PunchListDetail />} />
                  <Route path=":clientName/:projectId/punch-lists/templates/new" element={<TemplateBuilder />} />
                  <Route path=":clientName/:projectId/punch-lists/templates/:templateId" element={<TemplateBuilder />} />
                </Routes>
              </PropertyProvider>
            }
          />
        </Route>

        {/* Catch all route for 404s */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
